import { TaskV1TaskActionsServiceApprove, TaskV1TaskActionsServiceDeny } from 'c1gen/permissions'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1ToggleButtons } from 'components/core/RHKFormComponents/C1ToggleButtons'
import { TaskPrimaryActionsV2Variant } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { useHasPermission } from 'components/no-ui/capability'
import {
    ApproveWithCommentPendingActionDialog,
} from 'components/pages/task/details/task-content-v2/common/ApproveWithCommentPendingActionDialog'
import {
    DenyWithCommentPendingActionDialog,
} from 'components/pages/task/details/task-content-v2/common/DenyWithCommentPendingActionDialog'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { useSimpleModal } from 'lib/modals'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { PendingActionState, ReviewPendingDecision } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { usePendingTicketActionTracker } from 'redux_toolkit/lib/PendingTicketActionTracker'

type DecisionFormData = {
    decision: 'approve' | 'deny' | null
}

type ApproveOrDenyToggleProps = {
    task: TaskValue
    variant?: TaskPrimaryActionsV2Variant
    pendingActionState?: PendingActionState
}

export const ApproveOrDenyToggle: FC<ApproveOrDenyToggleProps> = ({
    task,
    variant = 'default',
    pendingActionState,
}) => {
    const intl = useIntl()
    const { open } = useSimpleModal()
    const toast = useAlert()
    const pendingTicketActionTracker = usePendingTicketActionTracker()
    const [isLoading, setIsLoading] = useState(false)

    const hasApprovePermission = useHasPermission(TaskV1TaskActionsServiceApprove)
    const hasDenyPermission = useHasPermission(TaskV1TaskActionsServiceDeny)
    const canApprove = hasApprovePermission && canActOn(task, TaskActionType.APPROVE)
    const canDeny = hasDenyPermission && canActOn(task, TaskActionType.DENY)

    // Determine current selection based on pending action state
    const selectedValue = useMemo((): 'approve' | 'deny' | null => {
        if (!pendingActionState?.state) return null

        let decision: ReviewPendingDecision | undefined
        if (pendingActionState.state.oneofKind === 'unlocked') {
            decision = pendingActionState.state.unlocked.decision
        } else if (pendingActionState.state.oneofKind === 'locked') {
            decision = pendingActionState.state.locked.decision
        }

        if (decision === ReviewPendingDecision.APPROVE) return 'approve'
        if (decision === ReviewPendingDecision.DENY) return 'deny'
        return null
    }, [pendingActionState])

    // Internal form to bridge C1ToggleButtons with external state
    const { control } = useForm<DecisionFormData>({
        defaultValues: { decision: selectedValue },
        values: { decision: selectedValue },
    })

    const handleChange = useCallback(async (value: 'approve' | 'deny' | null): Promise<void> => {
        if (!value) {
            // Clicking an already-selected toggle clears the pending state
            if (selectedValue && pendingTicketActionTracker) {
                setIsLoading(true)
                try {
                    await pendingTicketActionTracker.discardPendingAction(task)
                } catch (err) {
                    toast(
                        intl.formatMessage({
                            id: 'pending_action_discard_error',
                            defaultMessage: 'Error removing queued action',
                        }),
                        'error',
                    )
                } finally {
                    setIsLoading(false)
                }
            }
            return
        }

        const accessReviewId = task.accessReview?.id
        if (!accessReviewId) return

        const instance = task.policy?.current?.instance

        if (value === 'approve') {
            const requiresComment =
                instance && instance.oneofKind === 'approval' && instance.approval.approval?.requireApprovalReason

            if (requiresComment) {
                open(ApproveWithCommentPendingActionDialog, { task })()
                return
            }

            if (!pendingTicketActionTracker) return

            setIsLoading(true)
            try {
                await pendingTicketActionTracker.mutatePendingAction(task, ReviewPendingDecision.APPROVE, undefined, !!selectedValue)
            } catch (err) {
                toast(
                    intl.formatMessage({
                        id: 'pending_action_approve_error',
                        defaultMessage: 'Error queuing approval',
                    }),
                    'error',
                )
            } finally {
                setIsLoading(false)
            }
        } else if (value === 'deny') {
            if (instance && instance.oneofKind === 'approval' && instance.approval.approval?.requireDenialReason) {
                open(DenyWithCommentPendingActionDialog, { task })()
                return
            }

            if (!pendingTicketActionTracker) return

            setIsLoading(true)
            try {
                await pendingTicketActionTracker.mutatePendingAction(task, ReviewPendingDecision.DENY, undefined, !!selectedValue)
            } catch (err) {
                toast(
                    intl.formatMessage({
                        id: 'pending_action_deny_error',
                        defaultMessage: 'Error queuing denial',
                    }),
                    'error',
                )
            } finally {
                setIsLoading(false)
            }
        }
    }, [task, open, toast, intl, pendingTicketActionTracker, selectedValue])

    const options = useMemo(() => {
        const opts: { value: 'approve' | 'deny'; icon: React.ReactElement; color: 'success' | 'error' }[] = []
        if (canApprove) {
            opts.push({
                value: 'approve',
                icon: <C1Icon icon={ThumbsUp} size="sm" />,
                color: 'success',
            })
        }
        if (canDeny) {
            opts.push({
                value: 'deny',
                icon: <C1Icon icon={ThumbsDown} size="sm" />,
                color: 'error',
            })
        }
        return opts
    }, [canApprove, canDeny])

    if (!canApprove && !canDeny) {
        return null
    }

    return (
        <C1ToggleButtons
            control={control}
            name="decision"
            options={options}
            onChange={handleChange}
            disabled={isLoading}
            size={variant === 'icon-button' ? 'small' : 'medium'}
            allowDeselect
            aria-label={intl.formatMessage({ id: 'pending_action_toggle', defaultMessage: 'Approve or deny' })}
        />
    )
}
